<!--
  @component ServiceRecordDetail
  @description UC-REG-199 — Pantalla de detalle de una TRX. Se navega desde
    ServiceRecordList al clickear una fila. Muestra: identificación, bloque
    de origen (con link al lote de liquidación), montos y timeline de
    historial. Botón "Volver" para regresar al listado.

  @example (via router)
    router.push({ name: 'service-record-detail', params: { id: '...' } })
-->
<template>
  <div v-loading="isLoading" class="sr-detail">
    <!-- Breadcrumb sticky -->
    <div class="sr-detail__breadcrumb">
      <a class="sr-detail__back" @click="goBack">
        <i class="el-icon-arrow-left" />
        Transacciones
      </a>
      <span class="sr-detail__crumb-sep">/</span>
      <span class="sr-detail__crumb-current">
        {{ record ? record.transactionCode : "Detalle" }}
      </span>
    </div>

    <template v-if="record">
      <!-- HERO: identidad + estado + montos de un vistazo -->
      <div class="sr-detail__hero">
        <div class="sr-detail__hero-left">
          <div class="sr-detail__eyebrow">Transacción</div>
          <div class="sr-detail__code">{{ record.transactionCode || "—" }}</div>
          <div class="sr-detail__hero-sub">
            <span class="sr-detail__practice">
              <b>{{ record.practiceSnapshot.code || "—" }}</b>
              {{ record.practiceSnapshot.name || "" }}
            </span>
            <span class="sr-detail__hero-dot">·</span>
            <span>{{ record.performedAt | formatDate }}</span>
            <span class="sr-detail__hero-dot">·</span>
            <span>
              {{ record.memberSnapshot.lastName }},
              {{ record.memberSnapshot.firstName }}
              <span
                v-if="record.memberSnapshot.planName"
                class="sr-detail__hero-plan"
              >
                {{ record.memberSnapshot.planName }}
              </span>
            </span>
          </div>
        </div>

        <div class="sr-detail__hero-right">
          <span
            class="sr-detail__state"
            :class="'sr-detail__state--' + combinedStatus.kind"
          >
            {{ combinedStatus.label }}
          </span>
          <div class="sr-detail__amounts-inline">
            <div class="amt">
              <span class="amt-label">Declarado</span>
              <span class="amt-value">{{ formatMoney(declaredAmount) }}</span>
            </div>
            <div class="amt">
              <span class="amt-label">Débito</span>
              <span class="amt-value amt-value--debit"
                >−{{ formatMoney(debitAmount) }}</span
              >
            </div>
            <div class="amt amt--total">
              <span class="amt-label">Aprobado</span>
              <span class="amt-value">{{ formatMoney(acceptedAmount) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- CUERPO: metadata + timeline -->
      <div class="sr-detail__body">
        <aside class="sr-detail__side">
          <section class="sr-detail__card">
            <h3>Afiliado</h3>
            <dl>
              <dt>Nombre</dt>
              <dd>
                {{ record.memberSnapshot.lastName }},
                {{ record.memberSnapshot.firstName }}
              </dd>
              <dt v-if="record.memberSnapshot.memberId">Credencial</dt>
              <dd v-if="record.memberSnapshot.memberId">
                {{ record.memberSnapshot.memberId }}
              </dd>
              <dt>Plan</dt>
              <dd>{{ record.memberSnapshot.planName || "—" }}</dd>
            </dl>
          </section>

          <section class="sr-detail__card">
            <h3>Prestación</h3>
            <dl>
              <dt>Código</dt>
              <dd class="mono">{{ record.practiceSnapshot.code || "—" }}</dd>
              <dt>Denominación</dt>
              <dd>{{ record.practiceSnapshot.name || "—" }}</dd>
              <dt v-if="record.practiceSnapshot.category">Categoría</dt>
              <dd v-if="record.practiceSnapshot.category">
                {{ categoryLabel(record.practiceSnapshot.category) }}
              </dd>
              <dt>Fecha</dt>
              <dd>{{ record.performedAt | formatDateTime }}</dd>
            </dl>
          </section>

          <section class="sr-detail__card">
            <h3>Prestador</h3>
            <dl>
              <dt>Nombre</dt>
              <dd>{{ providerName }}</dd>
            </dl>
          </section>

          <section class="sr-detail__card sr-detail__card--origin">
            <h3>Origen de la liquidación</h3>
            <dl>
              <dt>Tipo</dt>
              <dd>
                <span class="chip" :class="originChipClass">
                  {{ originLabel }}
                </span>
              </dd>
              <template v-if="submissionRef">
                <dt>Lote</dt>
                <dd>
                  <a class="sr-detail__ref" @click="goToSubmission">
                    {{ submissionRef.label }}
                    <i class="el-icon-top-right" />
                  </a>
                </dd>
              </template>
              <dt>Registrada</dt>
              <dd>{{ record.createdAt | formatDateTime }}</dd>
            </dl>
          </section>
        </aside>

        <section class="sr-detail__main">
          <div class="sr-detail__main-header">
            <h3>Historial</h3>
            <span class="sr-detail__event-count">
              {{ events.length }}
              {{ events.length === 1 ? "evento" : "eventos" }}
            </span>
          </div>
          <div v-if="events.length === 0" class="sr-detail__empty">
            Sin eventos registrados todavía.
          </div>
          <div v-else class="timeline">
            <div
              v-for="(ev, idx) in events"
              :key="idx"
              class="event"
              :class="'event--' + ev.kind"
            >
              <div class="event-dot" />
              <div class="event-content">
                <div class="event-head">
                  <span class="event-title">{{ ev.title }}</span>
                  <span class="event-when">
                    {{ ev.at | formatDateTime }}
                    <template v-if="ev.actor">
                      · <b>{{ ev.actor }}</b>
                    </template>
                  </span>
                </div>
                <div v-if="ev.body" class="event-body">{{ ev.body }}</div>
                <a
                  v-if="ev.ref && ev.ref.kind === 'submission'"
                  class="event-ref"
                  @click="goToRef(ev.ref)"
                >
                  {{ ev.ref.label }}
                  <i class="el-icon-top-right" />
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </template>
  </div>
</template>

<script>
import { format } from "date-fns";
import { es } from "date-fns/locale";
import serviceRecordService from "@/services/service-record";

export default {
  name: "ServiceRecordDetail",

  filters: {
    /** @param {string|Date} v */
    formatDateTime(v) {
      if (!v) return "—";
      try {
        return format(new Date(v), "dd/MM/yyyy HH:mm", { locale: es });
      } catch {
        return "—";
      }
    },
    /** @param {string|Date} v */
    formatDate(v) {
      if (!v) return "—";
      try {
        return format(new Date(v), "dd/MM/yyyy", { locale: es });
      } catch {
        return "—";
      }
    },
  },

  data() {
    return {
      isLoading: false,
      record: null,
      events: [],
    };
  },

  computed: {
    /** @returns {string} */
    providerName() {
      const s = this.record && this.record.providerSnapshot;
      if (s && s.firstName) return `${s.lastName || ""}, ${s.firstName}`.trim();
      const p = this.record && this.record.provider;
      if (p && p.firstName) return `${p.lastName || ""}, ${p.firstName}`.trim();
      return "—";
    },
    /** @returns {string} */
    originLabel() {
      const o = this.record && this.record.origin;
      if (o === "authorization" && this.record.wasAudited)
        return "Autorización auditada";
      if (o === "authorization") return "Autorización";
      if (o === "liquidation_ex_post") return "Validación en liquidación";
      if (o === "liquidation_manual_override") return "Aceptación manual";
      return "Validación directa";
    },
    /** @returns {string} */
    originChipClass() {
      const o = this.record && this.record.origin;
      if (o === "authorization") return "chip--info";
      if (o === "liquidation_ex_post") return "chip--info";
      if (o === "liquidation_manual_override") return "chip--warn";
      return "chip--ok";
    },
    /** @returns {?Object} */
    submissionRef() {
      const sub = this.events.find((e) => e.ref && e.ref.kind === "submission");
      return (sub && sub.ref) || null;
    },
    /** @returns {number} */
    declaredAmount() {
      const r = this.record;
      return Number(
        (r &&
          (r.expectedAmount || (r.valorization && r.valorization.amount))) ||
          0
      );
    },
    /** @returns {number} */
    acceptedAmount() {
      const evOp = this.events.find((e) => e.type === "operator_accepted");
      if (evOp && evOp.amount != null) return Number(evOp.amount);
      return this.declaredAmount;
    },
    /** @returns {number} */
    debitAmount() {
      return Math.max(0, this.declaredAmount - this.acceptedAmount);
    },
    /** @returns {{kind: string, label: string}} */
    combinedStatus() {
      const r = this.record;
      if (!r) return { kind: "pending", label: "Sin liquidar" };
      if (r.status === "rejected" || r.status === "rules_rejected") {
        if (
          r.origin === "liquidation_ex_post" ||
          r.origin === "liquidation_manual_override"
        )
          return { kind: "void", label: "Anulada" };
        return { kind: "rejected", label: "Rechazada" };
      }
      const liq = r.liquidationStatus || "pending";
      if (liq === "paid") return { kind: "paid", label: "Pagada" };
      if (liq === "sent_for_payment")
        return { kind: "sent", label: "Enviada a pagar" };
      if (liq === "invoiced") return { kind: "invoiced", label: "Facturada" };
      if (liq === "liquidated")
        return { kind: "liquidated", label: "Liquidada" };
      return { kind: "pending", label: "Sin liquidar" };
    },
  },

  watch: {
    "$route.params.id"(id) {
      if (id) this.load();
    },
  },

  created() {
    this.load();
  },

  methods: {
    /** @description Carga detalle + historial en paralelo */
    async load() {
      const id = this.$route.params.id;
      if (!id) return;
      this.isLoading = true;
      this.record = null;
      this.events = [];
      try {
        const [rec, history] = await Promise.all([
          serviceRecordService.getById(id),
          serviceRecordService.getHistory(id),
        ]);
        this.record = rec;
        this.events = history.events || [];
      } catch (e) {
        this.$message.error("Error al cargar detalle de la transacción");
      } finally {
        this.isLoading = false;
      }
    },
    /** @param {number} n */
    formatMoney(n) {
      if (n == null) return "$0";
      return `$${Number(n).toLocaleString("es-AR")}`;
    },
    /** @param {string} c */
    categoryLabel(c) {
      return (
        {
          consultation: "Consulta",
          lab: "Laboratorio",
          imaging: "Imágenes",
          surgery: "Cirugía",
          dental: "Odontología",
          mental_health: "Salud mental",
          rehabilitation: "Rehabilitación",
          emergency: "Urgencias",
          hospitalization: "Internación",
          maternity: "Maternidad",
          medication: "Medicación",
          supply: "Insumos",
          prosthesis: "Prótesis",
          other: "Otros",
        }[c] ||
        c ||
        "—"
      );
    },
    /** @description Volver al listado */
    goBack() {
      this.$router.push({ name: "service-record-list" });
    },
    /** @description Navega al lote de liquidación */
    goToSubmission() {
      if (!this.submissionRef || !this.submissionRef.id) return;
      this.$router.push({
        name: "liquidation-submission-detail",
        params: { id: this.submissionRef.id },
      });
    },
    /** @param {{kind:string,id:string}} ref */
    goToRef(ref) {
      if (ref.kind === "submission") this.goToSubmission();
    },
  },
};
</script>

<style scoped lang="scss">
$bg: #f5f7fb;
$surface: #ffffff;
$border: #e4e8ef;
$border-strong: #cdd4de;
$ink: #1a1f36;
$ink-2: #475569;
$muted: #94a3b8;
$accent: #1e3a8a;
$accent-soft: #eef2ff;

.sr-detail {
  font-family: "Plus Jakarta Sans", sans-serif;
  color: $ink;
  padding: 8px 0 40px;
}

/* --- Breadcrumb --- */
.sr-detail__breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  color: $muted;
  margin-bottom: 16px;
}
.sr-detail__back {
  color: $ink-2;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 500;
  &:hover {
    color: $accent;
  }
}
.sr-detail__crumb-sep {
  color: $border-strong;
}
.sr-detail__crumb-current {
  font-family: monospace;
  color: $ink;
  font-weight: 600;
}

/* --- Hero --- */
.sr-detail__hero {
  background: $surface;
  border: 1px solid $border;
  border-radius: 14px;
  padding: 24px 28px;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 24px;
  align-items: center;
  margin-bottom: 20px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
}
@media (max-width: 900px) {
  .sr-detail__hero {
    grid-template-columns: 1fr;
  }
}

.sr-detail__eyebrow {
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: $muted;
  font-weight: 600;
  margin-bottom: 6px;
}
.sr-detail__code {
  font-family: monospace;
  font-size: 26px;
  color: $accent;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.1;
}
.sr-detail__hero-sub {
  margin-top: 10px;
  color: $ink-2;
  font-size: 13px;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
}
.sr-detail__practice b {
  font-family: monospace;
  color: $accent;
  font-weight: 600;
  margin-right: 6px;
}
.sr-detail__hero-dot {
  color: $border-strong;
}
.sr-detail__hero-plan {
  background: $accent-soft;
  color: $accent;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  margin-left: 4px;
}

.sr-detail__hero-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 14px;
}
@media (max-width: 900px) {
  .sr-detail__hero-right {
    align-items: flex-start;
  }
}

.sr-detail__amounts-inline {
  display: flex;
  gap: 20px;
  align-items: baseline;
}
.amt {
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: right;
}
.amt-label {
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: $muted;
  font-weight: 600;
}
.amt-value {
  font-family: monospace;
  font-variant-numeric: tabular-nums;
  font-size: 15px;
  color: $ink;
  font-weight: 600;
}
.amt-value--debit {
  color: #b91c1c;
}
.amt--total .amt-value {
  color: #065f46;
  font-size: 20px;
}

/* --- Estado chip grande --- */
.sr-detail__state {
  padding: 5px 14px;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 600;
  border: 1px solid transparent;
  white-space: nowrap;
}
.sr-detail__state--pending {
  background: #f1f5f9;
  color: #475569;
  border-color: #cbd5e1;
}
.sr-detail__state--liquidated {
  background: #ecfdf5;
  color: #065f46;
  border-color: #a7f3d0;
}
.sr-detail__state--invoiced {
  background: #dbeafe;
  color: #1e40af;
  border-color: #93c5fd;
}
.sr-detail__state--sent {
  background: #ede9fe;
  color: #5b21b6;
  border-color: #c4b5fd;
}
.sr-detail__state--paid {
  background: #d1fae5;
  color: #065f46;
  border-color: #6ee7b7;
}
.sr-detail__state--rejected {
  background: #fef2f2;
  color: #991b1b;
  border-color: #fecaca;
}
.sr-detail__state--void {
  background: #f1f5f9;
  color: #475569;
  border-color: #cbd5e1;
  text-decoration: line-through;
}

/* --- Cuerpo --- */
.sr-detail__body {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 20px;
}
@media (max-width: 900px) {
  .sr-detail__body {
    grid-template-columns: 1fr;
  }
}

/* --- Sidebar cards --- */
.sr-detail__side {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.sr-detail__card {
  background: $surface;
  border: 1px solid $border;
  border-radius: 12px;
  padding: 18px 20px;

  h3 {
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: 0.09em;
    color: $muted;
    font-weight: 600;
    margin: 0 0 12px;
  }
  dl {
    margin: 0;
    display: grid;
    grid-template-columns: 100px 1fr;
    gap: 8px 12px;
    font-size: 13px;
  }
  dt {
    color: $muted;
    font-weight: 500;
  }
  dd {
    margin: 0;
    color: $ink;
    &.mono {
      font-family: monospace;
      color: $accent;
      font-weight: 600;
    }
  }
}
.sr-detail__card--origin {
  border-left: 3px solid $accent;
}

.sr-detail__ref {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: monospace;
  font-size: 12px;
  color: $accent;
  background: $accent-soft;
  padding: 3px 10px;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 600;
  transition: background 120ms;
  i {
    font-size: 12px;
  }
  &:hover {
    background: #dbe4ff;
  }
}

/* --- Timeline main --- */
.sr-detail__main {
  background: $surface;
  border: 1px solid $border;
  border-radius: 12px;
  padding: 22px 26px 28px;
}
.sr-detail__main-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 20px;
  padding-bottom: 14px;
  border-bottom: 1px solid $border;

  h3 {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: $ink-2;
    font-weight: 600;
    margin: 0;
  }
}
.sr-detail__event-count {
  font-size: 11.5px;
  color: $muted;
  font-weight: 500;
}

.sr-detail__empty {
  color: $muted;
  font-size: 13px;
  padding: 24px 0;
  text-align: center;
}

.chip {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
}
.chip--ok {
  background: #d1fae5;
  color: #065f46;
}
.chip--warn {
  background: #fef3c7;
  color: #92400e;
}
.chip--err {
  background: #fee2e2;
  color: #b91c1c;
}
.chip--info {
  background: #dbeafe;
  color: #1e40af;
}

/* --- Timeline --- */
.timeline {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.event {
  display: grid;
  grid-template-columns: 24px 1fr;
  gap: 12px;
  padding: 12px 4px 12px 0;
  position: relative;
  border-radius: 8px;
  transition: background 120ms;

  &:hover {
    background: #fafbfd;
  }

  /* línea vertical entre puntos */
  &:not(:last-child)::before {
    content: "";
    position: absolute;
    left: 11px;
    top: 32px;
    bottom: -6px;
    width: 1.5px;
    background: $border-strong;
  }
}

.event-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: $surface;
  border: 2px solid $border-strong;
  margin-top: 6px;
  z-index: 1;
  justify-self: center;
}
.event--ok .event-dot {
  border-color: #065f46;
  background: #d1fae5;
}
.event--warn .event-dot {
  border-color: #92400e;
  background: #fef3c7;
}
.event--err .event-dot {
  border-color: #b91c1c;
  background: #fee2e2;
}
.event--info .event-dot {
  border-color: #1e40af;
  background: #dbeafe;
}
.event--sent .event-dot {
  border-color: #5b21b6;
  background: #ede9fe;
}
.event--paid .event-dot {
  border-color: #065f46;
  background: #bbf7d0;
}

.event-content {
  min-width: 0;
}
.event-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 16px;
  margin-bottom: 4px;
  flex-wrap: wrap;
}
.event-title {
  font-weight: 600;
  color: $ink;
  font-size: 13.5px;
  letter-spacing: -0.01em;
}
.event-when {
  color: $muted;
  font-size: 11.5px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  b {
    color: $ink-2;
    font-weight: 500;
  }
}
.event-body {
  color: $ink-2;
  font-size: 12.5px;
  line-height: 1.55;
}
.event-ref {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  font-family: monospace;
  font-size: 11.5px;
  color: $accent;
  background: $accent-soft;
  padding: 3px 10px;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 600;
  transition: background 120ms;
  i {
    font-size: 12px;
  }
  &:hover {
    background: #dbe4ff;
  }
}
</style>
